import styled from 'styled-components'
import { colors, fontScope, radius, visuallyHidden } from '../tokens'

export const Card = styled.div`
  ${fontScope}
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-sizing: border-box;
  padding: 12px;
  border: 1px solid ${colors.border};
  border-radius: ${radius.input};
  background: ${colors.white};
`

export const Top = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

export const Identity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

export const TicketName = styled.span`
  font-size: 14px;
  font-weight: 700;
  line-height: normal;
  color: ${colors.ink950};
`

export const TicketMeta = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
  color: ${colors.ink500};
`

export const Middle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

export const QuantityText = styled.span`
  font-size: 12px;
  font-weight: 700;
  line-height: normal;
  color: ${colors.ink700};
  white-space: nowrap;
`

export const Divider = styled.span`
  width: 1px;
  height: 12px;
  background: ${colors.checkboxBorder};
`

export const MetaText = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
  color: ${colors.ink500};
  white-space: nowrap;
`

export const Values = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-top: 8px;
  border-top: 1px solid ${colors.border};
`

export const ValueBlock = styled.div<{ $end?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $end }) => ($end ? 'flex-end' : 'flex-start')};
  gap: 2px;
`

export const ValueLabel = styled.span`
  font-size: 10px;
  font-weight: 700;
  line-height: normal;
  text-transform: uppercase;
  color: ${colors.ink400};
  white-space: nowrap;
`

export const ValueText = styled.span`
  font-size: 12px;
  font-weight: 600;
  line-height: normal;
  color: ${colors.ink800};
  white-space: nowrap;
`

export const TotalText = styled.span`
  font-size: 13px;
  font-weight: 700;
  line-height: normal;
  color: ${colors.positive};
  white-space: nowrap;
`

export const SectionHeader = styled.div`
  ${fontScope}
  display: flex;
  flex-direction: column;
  gap: 6px;
`

export const SectionCard = styled.div`
  ${fontScope}
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  box-sizing: border-box;
  padding: 14px 16px;
  border: 1px solid ${colors.border};
  border-radius: ${radius.card};
  background: ${colors.white};
`

export const SectionToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: ${radius.card};
  }

  &:focus-visible {
    outline: none;
  }

  &:focus-visible::after {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }
`

export const SectionChevron = styled.img<{ $open: boolean }>`
  flex-shrink: 0;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  transition: transform 0.15s ease;
`

export const Hidden = styled.span`
  ${visuallyHidden}
`

export const MetricsGrid = styled.dl`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 12px;
  margin: 4px 0 2px;
`

export const Metric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

export const MetricLabel = styled.dt`
  font-size: 11px;
  font-weight: 700;
  line-height: normal;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: ${colors.ink400};
`

export const MetricValue = styled.dd<{ $low: boolean }>`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  line-height: normal;
  color: ${({ $low }) => ($low ? colors.dangerText : colors.ink900)};
  overflow-wrap: anywhere;
`

export const SectionDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

export const SectionName = styled.span`
  font-size: 18px;
  font-weight: 800;
  line-height: normal;
  color: ${colors.ink900};
`

export const SectionSummary = styled.span`
  font-size: 13px;
  font-weight: 400;
  line-height: normal;
  color: ${colors.ink500};
`

export const SectionProgress = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const TONE_TEXT = { positive: colors.positive, warning: colors.warningText, danger: colors.dangerText }

export const SectionProgressLabel = styled.span<{ $tone: keyof typeof TONE_TEXT }>`
  font-size: 11px;
  font-weight: 700;
  line-height: normal;
  color: ${({ $tone }) => TONE_TEXT[$tone]};
`

export const SubtotalCard = styled.div`
  ${fontScope}
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-sizing: border-box;
  padding: 12px;
  border-radius: ${radius.input};
  background: ${colors.mobileSubtotal};
`

export const SubtotalTitle = styled.span`
  font-size: 12px;
  font-weight: 700;
  line-height: normal;
  color: ${colors.ink800};
`

export const SubtotalSides = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
  color: ${colors.ink600};
`

export const SubtotalTotalRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-top: 4px;
  border-top: 1px solid ${colors.checkboxBorder};
`

export const SubtotalTotalLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  line-height: normal;
  color: ${colors.ink700};
`

export const SubtotalTotalValue = styled.span`
  font-size: 14px;
  font-weight: 800;
  line-height: normal;
  color: ${colors.positive};
`

export const TotalCard = styled.div`
  ${fontScope}
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-sizing: border-box;
  padding: 16px;
  border-radius: ${radius.card};
  background: ${colors.totalBar};
`

export const TotalCardTitle = styled.span`
  font-size: 14px;
  font-weight: 700;
  line-height: normal;
  color: ${colors.white};
`

export const TotalCardSides = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  line-height: normal;
  color: ${colors.white};
`

export const TotalCardRevenue = styled.span`
  font-size: 14px;
  font-weight: 800;
  color: ${colors.positiveOnDark};
`
